'use client'

import type { AnalyticsDayBuckets } from '@sushiswap/graph-client/data-api'
import { useIsMounted } from '@sushiswap/hooks'
import format from 'date-fns/format'
import type { EChartOption } from 'echarts'
import ReactEchartsCore from 'echarts-for-react/lib/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { useTheme } from 'next-themes'
import { type FC, useCallback, useMemo } from 'react'
import { formatUSD } from 'sushi'
import { type EvmChainId, getEvmChainById } from 'sushi/evm'
import tailwindConfig from 'tailwind.config.js'
import resolveConfig from 'tailwindcss/resolveConfig'

const tailwind = resolveConfig(tailwindConfig)
const CHART_COLOR = (tailwind.theme?.colors?.blue as Record<string, string>)[
  '500'
]

interface TVLChart {
  data: AnalyticsDayBuckets
  chainId: EvmChainId
}

echarts.use([CanvasRenderer, LineChart, TooltipComponent, GridComponent])

export const TVLChart: FC<TVLChart> = ({ data, chainId }) => {
  const isMounted = useIsMounted()

  const { resolvedTheme } = useTheme()

  const [v3, combinedTVL, currentDate] = useMemo(() => {
    const xData = data.v3.map((data) => data.date * 1000)

    const v3 = xData
      .map((xData, i) => [xData, data.v3[i]?.liquidityUSD ?? 0])
      .reverse()

    const v3TVL = v3[v3.length - 1]?.[1] ?? 0
    const combinedTVL = v3TVL

    const currentDate = xData[0]

    return [v3, combinedTVL, currentDate]
  }, [data])

  const onMouseOver = useCallback((params: { data?: number[] }[]) => {
    if (!params[0].data) return ''

    const tvlNode = document.getElementById('hoveredTVL')
    const v3TVLNode = document.getElementById('hoveredV3TVL')
    const dateNode = document.getElementById('hoveredTVLDate')

    if (tvlNode) tvlNode.innerHTML = formatUSD(params[0].data[1])
    if (dateNode)
      dateNode.innerHTML = format(new Date(params[0].data[0]), 'dd MMM yyyy')
    if (v3TVLNode)
      v3TVLNode.innerHTML = params[0].data[1]
        ? formatUSD(params[0].data[1])
        : ''

    return ''
  }, [])

  const onMouseLeave = useCallback(() => {
    const tvlNode = document.getElementById('hoveredTVL')
    const v3TVLNode = document.getElementById('hoveredV3TVL')
    const dateNode = document.getElementById('hoveredTVLDate')

    if (tvlNode) tvlNode.innerHTML = formatUSD(combinedTVL)
    if (dateNode)
      dateNode.innerHTML = format(new Date(currentDate), 'dd MMM yyyy')
    if (v3TVLNode) v3TVLNode.innerHTML = ''
  }, [combinedTVL, currentDate])

  const DEFAULT_OPTION = useMemo<EChartOption>(
    () => ({
      tooltip: {
        trigger: 'axis',
        padding: 0,
        borderWidth: 0,
        axisPointer: {
          lineStyle: {
            type: 'solid',
          },
        },
        formatter: (params) =>
          onMouseOver(Array.isArray(params) ? params : [params]),
      },
      color: [CHART_COLOR],
      grid: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 40,
      },
      xAxis: [
        {
          type: 'time',
          splitLine: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          splitNumber: 3,
          axisLabel: {
            hideOverlap: true,
            showMinLabel: true,
            showMaxLabel: true,
            color: resolvedTheme === 'dark' ? 'white' : 'black',
            formatter: (value: number, index: number) => {
              const date = new Date(value)
              const label = `${date.toLocaleString('en-US', {
                month: 'short',
              })} ${date.getDate()}\n${date.getFullYear()}`

              return index === 0
                ? `{min|${label}}`
                : value > v3?.[v3.length - 2]?.[0]
                  ? `{max|${label}}`
                  : label
            },
            rich: {
              min: {
                padding: [0, 10, 0, 50],
              },
              max: {
                padding: [0, 50, 0, 10],
              },
            },
          },
        },
      ],
      yAxis: [
        {
          show: false,
        },
      ],
      series: [
        {
          name: 'v3',
          type: 'line',
          stack: 'v3',
          smooth: true,
          lineStyle: {
            width: 0,
          },
          showSymbol: false,
          areaStyle: {
            color: CHART_COLOR,
            opacity: 1,
          },
          data: v3,
          z: 1,
        },
      ],
    }),
    [onMouseOver, v3, resolvedTheme],
  )

  return (
    <div>
      <div className="flex flex-col gap-3">
        <span className="text-muted-foreground text-sm">
          {getEvmChainById(chainId).name} TVL
        </span>
        <div className="flex justify-between">
          <div className="flex flex-col gap-3">
            <div className="text-3xl font-medium">
              <span id="hoveredTVL">{formatUSD(combinedTVL)}</span>
            </div>
            <div>
              <div
                id="hoveredTVLDate"
                className="text-sm text-gray-500 dark:text-slate-500 min-h-[40px]"
              >
                {isMounted && currentDate
                  ? format(new Date(currentDate), 'dd MMM yyyy')
                  : ''}
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex justify-between items-center gap-2 text-sm">
              <span id="hoveredV3TVL" />
              <span className="flex gap-1 items-center">
                <span className="font-medium">v3</span>
                <span
                  className="rounded-[4px] w-3 h-3"
                  style={{ backgroundColor: CHART_COLOR }}
                />
              </span>
            </div>
          </div>
        </div>
      </div>
      <ReactEchartsCore
        option={DEFAULT_OPTION}
        echarts={echarts}
        style={{ height: 400 }}
        onEvents={{
          globalout: onMouseLeave,
        }}
      />
    </div>
  )
}
